<script>
import ClassificationBadge from './ClassificationBadge.vue';
import CustomClassificationBadge from './CustomClassificationBadge.vue';
import Reply from './Reply.vue';
import MetadataBadge from './MetadataBadge.vue';
import Icon from 'next/icon/Icon.vue';
import { formatDate } from 'dashboard/helper/dateHelper.js';

export default {
  name: 'Comment',
  components: {
    ClassificationBadge,
    CustomClassificationBadge,
    Reply,
    MetadataBadge,
    Icon,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    uiFlags: {
      type: Object,
      required: true,
    },
    hideComment: {
      type: Function,
      required: true,
    },
    deleteComment: {
      type: Function,
      required: true,
    },
    openReplyPopup: {
      type: Function,
      required: true,
    },
    openDmPopup: {
      type: Function,
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showReplies: false,
      moderationInFlight: false,
    };
  },
  computed: {
    isCardInteractionLocked() {
      return this.moderationInFlight;
    },
    metadataType() {
      return metadataType => {
        switch (metadataType?.purchase_intention) {
          case true:
            return 'PURCHASE_INTENTION';
          default:
            return metadataType;
        }
      };
    },
    showMetadataBadge() {
      return (
        !!this.comment.from_name && !!this.comment.metadata?.purchase_intention
      );
    },
    isMentionedComment() {
      return this.comment.metadata?.type === 'MENTION';
    },
  },
  methods: {
    async runHideForTarget(commentId) {
      if (this.moderationInFlight) return;
      this.moderationInFlight = true;
      try {
        await this.hideComment(commentId);
      } finally {
        this.moderationInFlight = false;
      }
    },
    async runDeleteForTarget(commentId) {
      if (this.moderationInFlight) return;
      this.moderationInFlight = true;
      try {
        await this.deleteComment(commentId);
      } finally {
        this.moderationInFlight = false;
      }
    },
    toggleReplies() {
      if (this.isCardInteractionLocked) return;
      this.showReplies = !this.showReplies;
    },
    formatDate(date) {
      return formatDate(date);
    },
    handleReplyOpenFromChild(payload) {
      if (this.isCardInteractionLocked) return;
      const replyId = payload?.replyId ?? null;
      const authorName = payload?.authorName ?? null;
      this.openReplyPopup(replyId, authorName);
    },
    handleMainReplyClick() {
      if (this.isCardInteractionLocked) return;
      this.openReplyPopup(this.comment.id);
    },
    handleMainDmClick() {
      if (this.isCardInteractionLocked || !this.openDmPopup) return;
      this.openDmPopup(this.comment.id);
    },
    handleDmOpenFromChild(payload) {
      if (this.isCardInteractionLocked || !this.openDmPopup) return;
      const commentId = payload?.commentId ?? payload?.replyId ?? null;
      const authorName = payload?.authorName ?? null;
      if (!commentId) return;
      this.openDmPopup(commentId, authorName);
    },
  },
};
</script>

<template>
  <div class="relative w-full" :class="{ 'opacity-60': comment.hidden }">
    <div
      v-if="isCardInteractionLocked"
      class="absolute inset-0 z-[1] flex items-center justify-center bg-white/70 dark:bg-slate-900/60 pointer-events-auto rounded-lg"
      role="status"
      :aria-label="$t('COMMENTS.MODERATION_CARD.ACTIONS_PENDING')"
    >
      <Icon
        icon="i-lucide-loader-2"
        class="size-8 text-woot-500 motion-safe:animate-spin"
      />
    </div>

    <div
      class="flex items-start space-x-4 w-full px-6 py-3"
      :class="{
        'border-l-4 border-l-woot-500 bg-[#F9F9FB] dark:bg-slate-600': selected,
      }"
    >
      <div
        class="rounded-full bg-[#0F6CBD] h-10 w-10 p-2 flex justify-center items-center shrink-0"
      >
        <svg
          width="21"
          height="20"
          viewBox="0 0 21 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.5 2C8.29086 2 6.5 3.79086 6.5 6C6.5 8.20914 8.29086 10 10.5 10C12.7091 10 14.5 8.20914 14.5 6C14.5 3.79086 12.7091 2 10.5 2ZM7.5 6C7.5 4.34315 8.84315 3 10.5 3C12.1569 3 13.5 4.34315 13.5 6C13.5 7.65685 12.1569 9 10.5 9C8.84315 9 7.5 7.65685 7.5 6ZM5.50873 11C4.40315 11 3.5 11.8869 3.5 13C3.5 14.6912 4.33281 15.9663 5.63499 16.7966C6.91697 17.614 8.64526 18 10.5 18C12.3547 18 14.083 17.614 15.365 16.7966C16.6672 15.9663 17.5 14.6912 17.5 13C17.5 11.8956 16.6045 11 15.5 11L5.50873 11ZM4.5 13C4.5 12.4467 4.94786 12 5.50873 12L15.5 12C16.0522 12 16.5 12.4478 16.5 13C16.5 14.3088 15.8777 15.2837 14.8274 15.9534C13.7568 16.636 12.2351 17 10.5 17C8.76489 17 7.24318 16.636 6.17262 15.9534C5.12226 15.2837 4.5 14.3088 4.5 13Z"
            fill="white"
          />
        </svg>
      </div>
      <div class="flex-1 space-y-1 min-w-0">
        <div class="flex items-center gap-4 flex-wrap">
          <h3 class="text-sm font-semibold flex items-center gap-2 flex-wrap">
            <span>{{ comment.from_name }}</span>
            <span
              v-if="comment.hidden"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
            >
              <Icon icon="i-lucide-eye-off" class="size-3" />
              {{ $t('COMMENTS.REPLY.HIDDEN') }}
            </span>
            <a
              v-if="comment.source_type === 'INSTAGRAM' && comment.from_name"
              :href="`https://www.instagram.com/${encodeURIComponent(comment.from_name.trim())}/`"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center text-woot-500 opacity-80 hover:opacity-100 transition-opacity"
              :class="{
                'pointer-events-none opacity-50': isCardInteractionLocked,
              }"
              :aria-label="$t('COMMENTS.CARD.OPEN_INSTAGRAM_PROFILE')"
              :title="$t('COMMENTS.CARD.VIEW_ON_INSTAGRAM')"
              @click.stop
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="opacity-90"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient
                    id="instagramGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stop-color="#fccc63" />
                    <stop offset="20%" stop-color="#fbad50" />
                    <stop offset="40%" stop-color="#e95950" />
                    <stop offset="60%" stop-color="#d53f8c" />
                    <stop offset="80%" stop-color="#a437b4" />
                    <stop offset="100%" stop-color="#833ab4" />
                  </linearGradient>
                </defs>

                <path
                  d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z"
                  stroke="url(#instagramGradient)"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="5"
                  stroke="url(#instagramGradient)"
                  stroke-width="1.8"
                />
                <circle cx="17" cy="7" r="1.3" fill="url(#instagramGradient)" />
              </svg>
            </a>
            <a
              v-if="comment.source_type === 'FACEBOOK' && comment.source_id"
              :href="`https://www.facebook.com/${comment.hidden ? comment.source_id.split('_')[0] : comment.source_id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center text-woot-500 opacity-80 hover:opacity-100 transition-opacity"
              :class="{
                'pointer-events-none opacity-50': isCardInteractionLocked,
              }"
              :aria-label="$t('COMMENTS.CARD.OPEN_FACEBOOK_COMMENT')"
              :title="$t('COMMENTS.CARD.VIEW_ON_FACEBOOK')"
              @click.stop
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="opacity-90"
                aria-hidden="true"
              >
                <path
                  d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3V2z"
                  stroke="#1877F2"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </a>
          </h3>
          <div class="flex flex-wrap items-center gap-2">
            <ClassificationBadge :type="comment.classification" />
            <MetadataBadge
              v-if="showMetadataBadge"
              :type="metadataType(comment.metadata)"
            />
            <CustomClassificationBadge
              v-if="
                Array.isArray(comment.metadata?.custom_classifications) &&
                comment.metadata.custom_classifications.length
              "
              :items="comment.metadata.custom_classifications"
            />
          </div>
        </div>

        <p
          class="text-sm sm:text-base text-slate-700 dark:text-slate-300 break-words"
        >
          {{ comment.message }}
        </p>

        <div
          class="flex flex-col justify-between sm:flex-row sm:items-center gap-3"
        >
          <span class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {{ formatDate(comment.created_time) }}
          </span>
          <div class="flex gap-3 sm:gap-4 text-xs sm:text-sm">
            <button
              type="button"
              class="text-woot-500 hover:text-woot-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click.stop.prevent="handleMainReplyClick"
            >
              <Icon icon="i-lucide-reply" class="size-3.5" />
              {{ $t('COMMENTS.CARD.REPLY') }}
            </button>
            <button
              v-if="openDmPopup"
              type="button"
              class="text-woot-500 hover:text-woot-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click.stop.prevent="handleMainDmClick"
            >
              <Icon icon="i-lucide-mail" class="size-3.5" />
              {{ $t('COMMENTS.CARD.SEND_DM') }}
            </button>
            <button
              v-if="!isMentionedComment"
              type="button"
              class="text-slate-600 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click="runDeleteForTarget(comment.id)"
            >
              <Icon icon="i-lucide-trash-2" class="size-3.5" />
              {{ $t('COMMENTS.CARD.DELETE') }}
            </button>
            <button
              v-if="!isMentionedComment"
              type="button"
              class="text-slate-600 hover:text-woot-500 dark:text-slate-400 dark:hover:text-woot-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click="runHideForTarget(comment.id)"
            >
              <Icon
                :icon="comment.hidden ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                class="size-3.5"
              />
              {{
                comment.hidden
                  ? $t('COMMENTS.CARD.SHOW')
                  : $t('COMMENTS.CARD.HIDE')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Replies Section -->
    <div
      v-if="comment.replies && comment.replies.length > 0"
      class="border-t border-slate-200 dark:border-slate-700"
    >
      <div class="px-4 sm:px-6 py-3">
        <button
          type="button"
          class="text-sm text-woot-500 hover:text-woot-700 dark:text-woot-400 dark:hover:text-woot-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
          :disabled="isCardInteractionLocked"
          @click="toggleReplies"
        >
          {{
            showReplies
              ? $t('COMMENTS.CARD.HIDE_REPLIES')
              : $t('COMMENTS.CARD.VIEW_REPLIES')
          }}
        </button>
      </div>
      <div
        v-if="showReplies"
        class="px-4 sm:px-6 pb-4 space-y-3 bg-slate-50 dark:bg-slate-800/50"
      >
        <div
          v-for="reply in comment.replies"
          :key="reply.id"
          class="pl-4 sm:pl-8 border-l-2 border-slate-300 dark:border-slate-600"
        >
          <Reply
            :reply="reply"
            :ui-flags="uiFlags"
            :show-dm-action="!!openDmPopup"
            :parent-moderation-busy="isCardInteractionLocked"
            :post-source-type="comment.source_type"
            :parent-hidden="comment.hidden"
            :hide-comment="() => runHideForTarget(reply.id)"
            :delete-comment="() => runDeleteForTarget(reply.id)"
            @open-reply-popup="payload => handleReplyOpenFromChild(payload)"
            @open-dm-popup="payload => handleDmOpenFromChild(payload)"
          />
          <MetadataBadge
            v-if="reply?.metadata?.purchase_intention"
            :type="metadataType(reply.metadata)"
            class="mt-2"
          />
        </div>
      </div>
    </div>
  </div>
</template>
